import './ComponentsPreview.css';
import {
  useAllItems,
  useConversation,
  type ComponentProperty,
  type ComponentPropertyValue,
  type ConversationItem,
  type FunctionParameter,
} from '@/services/ConversationService';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useVersions } from './VersionsPreview';
import {
  ColorIndicator,
  Column,
  Font,
  PaletteInput,
  Row,
  TextColorOption,
  ThinBorderStyle,
  ToggleSwitch,
  TypeaheadInput,
} from './DesignSystem';
import { Spacer, useColorPreview } from './ColorPreview';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useScreenPreview, type ScreenID } from './ScreenPreview';
import { useTextStyle, type TextStyle } from './TypographyPreview';
import { SystemImage } from './PropertyValueImage';
import {
  ComponentPreviewDebugBoundary,
  useComponentPreviewDebug,
} from './ComponentPreviewDebugPalette';
import { resolveButtonSymbolIcon } from './button-symbols';

export type ComponentID = string;
export function NewComponentID(): ComponentID {
  return crypto.randomUUID();
}

type ComponentsPreviewReducerState = {
  functionParameterPreviewValuesByID: Record<
    FunctionComponentID,
    Record<string, ComponentPropertyValue | undefined>
  >;
  componentPropertyOverridesByID: Record<ComponentID, Array<ComponentProperty>>;
};

type ComponentsPreviewAction =
  | {
      type: 'update-parameter-preview-value';
      functionID: FunctionComponentID;
      parameterName: string;
      value?: ComponentPropertyValue;
    }
  | {
      type: 'update-preview-state';
      componentID: ComponentID;
      property: ComponentProperty;
    };

type ComponentsPreviewDispatch = (_: ComponentsPreviewAction) => void;

type LeafComponentType =
  | 'label'
  | 'button'
  | 'textInput'
  | 'binaryToggle'
  | 'image'
  | 'segmentedControl'
  | 'function'
  | 'list'
  | 'tabBar';

type ContainerComponentType = 'stack' | 'group';

export type Component =
  | {
      id: ComponentID;
      type: LeafComponentType;
      properties: ComponentProperty[];
      canHaveChildren: false;
    }
  | {
      id: ComponentID;
      type: ContainerComponentType;
      childIDs?: ComponentID[];
      properties: ComponentProperty[];
      canHaveChildren: true;
    };

export const ALL_COMPONENT_TYPES: Component['type'][] = [
  'label',
  'button',
  'textInput',
  'binaryToggle',
  'image',
  'segmentedControl',
  'function',
  'list',
  'tabBar',
  'stack',
  'group',
];

export type FunctionComponentID = string;
type FunctionComponent = {
  id: FunctionComponentID;
  name: string;
  templateID: ComponentID;
  parameters: FunctionParameter[];
};
type ComponentStyleDefinition = {
  id: string;
  name: string;
  properties: ComponentProperty[];
};
type ComponentsPreviewComputedState = {
  functionIDOrdering: FunctionComponentID[];
  functionComponentsByID: Record<FunctionComponentID, FunctionComponent>;
  componentsByID: Record<ComponentID, Component>;
  componentStylesByName: Record<string, ComponentStyleDefinition>;
};

type ComponentsPreviewContextProps = {
  state: ComponentsPreviewReducerState;
  dispatch: ComponentsPreviewDispatch;
};

type ComponentsPreviewExternalContextProps = {
  state: ComponentsPreviewReducerState & ComponentsPreviewComputedState;
  dispatch: ComponentsPreviewDispatch;
};

export const ComponentsPreviewContext =
  createContext<ComponentsPreviewContextProps | null>(null);

export function useComponentsPreview(): ComponentsPreviewExternalContextProps {
  const context = useContext(ComponentsPreviewContext);
  if (context === null) {
    throw new Error(
      'useComponentsPreview must be used within a ComponentsPreviewBoundary',
    );
  }

  const { dispatch: conversationDispatch } = useConversation();
  const { state, dispatch } = context;
  const {
    state: { activeVersionID },
  } = useVersions();
  const allItems = useAllItems({ activeVersionID });

  const computedState = useMemo(() => {
    return getComponentsFromConversation(allItems);
  }, [allItems]);

  return {
    state: {
      ...state,
      ...computedState,
    },
    dispatch,
  };
}

function DefaultProperties(
  componentType: Component['type'],
): ComponentProperty[] {
  // TODO: Generate with deus!
  switch (componentType) {
    case 'label':
      return [
        {
          name: 'text',
          value: { type: 'string', value: null },
        },
      ];
    case 'function':
    case 'button':
    case 'textInput':
    case 'binaryToggle':
    case 'image':
      return [
        {
          name: 'src',
          value: { type: 'string', value: null },
        },
        {
          name: 'cornerRadius',
          value: { type: 'int', value: null },
        },
      ];
    case 'segmentedControl':
    case 'stack':
      return [
        {
          name: 'childLayoutAxis',
          value: { type: 'string', value: null },
        },
      ];
    case 'group':
      return [];
    case 'list':
      return [
        {
          name: 'functionName',
          value: { type: 'string', value: null },
        },
        { name: 'items', value: { type: 'array', value: null } },
        { name: 'spacing', value: { type: 'int', value: null } },
      ];
    case 'tabBar': {
      return [
        {
          name: 'tabs',
          value: { type: 'array', value: null },
        },
      ];
    }
  }
}

function getComponentsFromConversation(
  items: ConversationItem[],
): ComponentsPreviewComputedState {
  return items.reduce<ComponentsPreviewComputedState>(
    (acc, e) => {
      switch (e.type) {
        case 'declareFunctionComponent': {
          const next = acc;
          next.functionComponentsByID[e.id] = e;
          let functionIDOrdering = acc.functionIDOrdering;
          if (functionIDOrdering.filter((id) => id === e.id).length === 0) {
            functionIDOrdering = functionIDOrdering.concat(e.id);
          }
          return {
            ...next,
            functionIDOrdering,
          };
        }
        case 'removeFunctionComponent': {
          const matchingFunctions = Object.values(
            acc.functionComponentsByID,
          ).filter((fn) => fn.name === e.name);

          if (matchingFunctions.length === 0) {
            return acc;
          }

          const removedFunctionIDs = new Set(
            matchingFunctions.map((fn) => fn.id),
          );
          const nextFunctionComponentsByID = {
            ...acc.functionComponentsByID,
          };
          for (const fn of matchingFunctions) {
            delete nextFunctionComponentsByID[fn.id];
          }

          const functionIDOrdering = acc.functionIDOrdering.filter(
            (id) => !removedFunctionIDs.has(id),
          );

          const removedComponentIDs = new Set<ComponentID>();
          const collectSubtree = (componentID: ComponentID) => {
            if (removedComponentIDs.has(componentID)) {
              return;
            }
            const component = acc.componentsByID[componentID];
            if (component === undefined) {
              return;
            }

            removedComponentIDs.add(componentID);
            const childIDs = component.canHaveChildren
              ? (component.childIDs ?? [])
              : [];
            childIDs.forEach(collectSubtree);
          };

          for (const fn of matchingFunctions) {
            collectSubtree(fn.templateID);
          }

          if (removedComponentIDs.size === 0) {
            return {
              ...acc,
              functionComponentsByID: nextFunctionComponentsByID,
              functionIDOrdering,
            };
          }

          const nextComponentsByID: Record<ComponentID, Component> = {};

          for (const id of Object.keys(acc.componentsByID)) {
            if (removedComponentIDs.has(id)) {
              continue;
            }
            const component = acc.componentsByID[id];
            if (component === undefined) {
              continue;
            }

            if (!component.canHaveChildren) {
              nextComponentsByID[id] = component;
              continue;
            }
            const existingChildIDs = component.childIDs ?? [];
            const nextChildIDs = existingChildIDs.filter(
              (childID) => !removedComponentIDs.has(childID),
            );
            nextComponentsByID[id] =
              nextChildIDs.length === existingChildIDs.length
                ? component
                : { ...component, childIDs: nextChildIDs };
          }

          return {
            ...acc,
            functionComponentsByID: nextFunctionComponentsByID,
            functionIDOrdering,
            componentsByID: nextComponentsByID,
          };
        }
        case 'defineComponentStyle': {
          return {
            ...acc,
            componentStylesByName: {
              ...acc.componentStylesByName,
              [e.name]: {
                id: e.id,
                name: e.name,
                properties: e.properties,
              },
            },
          };
        }
        case 'removeComponentStyle': {
          if (acc.componentStylesByName[e.name] === undefined) {
            return acc;
          }
          const next = { ...acc.componentStylesByName };
          delete next[e.name];
          return {
            ...acc,
            componentStylesByName: next,
          };
        }
        case 'writeProperties': {
          const existingComponent = acc.componentsByID[e.componentID];
          if (existingComponent === undefined) {
            // NEXT: Check the function definitions

            // no-op
            return acc;
          }

          return {
            ...acc,
            componentsByID: {
              ...acc.componentsByID,
              [e.componentID]: {
                ...existingComponent,
                properties: e.properties.concat(existingComponent.properties),
              },
            },
          };
        }
        case 'createComponent': {
          // NOTE: Type inference gets confused because canHavechildren is dynamically determined. Explicitly cast to shut type inference up.
          const canHaveChildren =
            e.componentType === 'stack' || e.componentType === 'group';

          const properties = DefaultProperties(e.componentType);

          const component: Component = (() => {
            if (canHaveChildren) {
              return {
                id: e.id,
                type: e.componentType as ContainerComponentType,
                properties,
                canHaveChildren: true,
              };
            }

            return {
              id: e.id,
              type: e.componentType as LeafComponentType,
              properties,
              canHaveChildren: false,
            };
          })();

          return {
            ...acc,
            componentsByID: {
              ...acc.componentsByID,
              [e.id]: component,
            },
          };
        }
        case 'addChildComponent': {
          switch (e.parent.type) {
            case 'component': {
              const existingComponent = acc.componentsByID[e.parent.id];
              if (
                existingComponent === undefined ||
                !existingComponent.canHaveChildren
              ) {
                return acc;
              }

              const existingChildren = existingComponent.childIDs ?? [];

              return {
                ...acc,
                componentsByID: {
                  ...acc.componentsByID,
                  [e.parent.id]: {
                    ...existingComponent,
                    childIDs: existingChildren.concat(e.childID),
                  },
                },
              };
            }
            case 'screen':
              // TODO: Support screens
              return acc;
          }
        }
        case 'defineColor':
        case 'removeColor':
        case 'updateColorPalette':
        case 'updateTextStyles':
        case 'message':
        case 'defineTextStyle':
        case 'removeTextStyle':
        case 'addScreen':
        case 'removeScreen':
        case 'describeScreen':
        case 'connect':
        case 'defineComponentStyle':
        case 'removeComponentStyle':
        case 'createVersion':
          // no-op
          return acc;
      }
    },
    {
      functionIDOrdering: [],
      functionComponentsByID: {},
      componentsByID: {},
      componentStylesByName: {},
    },
  );
}

export function ComponentsPreviewBoundary({
  children,
}: React.PropsWithChildren) {
  function reducer(
    state: ComponentsPreviewReducerState,
    action: ComponentsPreviewAction,
  ): ComponentsPreviewReducerState {
    switch (action.type) {
      case 'update-parameter-preview-value': {
        const existing =
          state.functionParameterPreviewValuesByID[action.functionID] ?? {};
        return {
          ...state,
          functionParameterPreviewValuesByID: {
            ...state.functionParameterPreviewValuesByID,
            [action.functionID]: {
              ...existing,
              [action.parameterName]: action.value,
            },
          },
        };
      }
      case 'update-preview-state': {
        const existingPropertyOverrides =
          state.componentPropertyOverridesByID[action.componentID] ?? [];

        let propertyOverrides = existingPropertyOverrides;
        let replacedPropertyOverride = false;
        propertyOverrides = existingPropertyOverrides.map((p) => {
          if (p.name === action.property.name) {
            replacedPropertyOverride = true;
            return action.property;
          }
          return p;
        });
        if (!replacedPropertyOverride) {
          propertyOverrides = [action.property].concat(propertyOverrides);
        }
        return {
          ...state,
          componentPropertyOverridesByID: {
            ...state.componentPropertyOverridesByID,
            [action.componentID]: propertyOverrides,
          },
        };
      }
    }
  }
  const [state, dispatch] = useReducer(reducer, {
    functionParameterPreviewValuesByID: {},
    componentPropertyOverridesByID: {},
  });

  return (
    <ComponentsPreviewContext.Provider value={{ state, dispatch }}>
      <ComponentPreviewDebugBoundary>{children}</ComponentPreviewDebugBoundary>
    </ComponentsPreviewContext.Provider>
  );
}

function readProperty({
  name,
  component,
  parameters,
}: {
  name: string;
  component: Component;
  parameters?: FunctionParameter[];
}): ComponentPropertyValue | undefined {
  const properties = component.properties;

  const defaultValue = (parameters ?? [])
    .filter(
      (p) =>
        p.binding.propertyName === name &&
        p.binding.componentID === undefined &&
        p.defaultValue !== undefined,
    )
    .map((p) => p.defaultValue)[0];

  const boundParameter = (parameters ?? []).find((parameter) => {
    // Resolve binding
    return (
      parameter.binding.componentID === component.id &&
      parameter.binding.propertyName === name
    );
  });

  // TODO(LAB-467): For now, changing the FunctionComponent's value in the form updates the `defaultValue`, rather than just a preview state. This means the bound parameter's value will never be set in the ComponentPalette, and we'll always fallback to the defaultValue case.
  if (
    boundParameter !== undefined &&
    boundParameter.defaultValue !== undefined
  ) {
    return boundParameter.defaultValue;
  }

  if (defaultValue !== undefined) {
    return defaultValue;
  }

  const matchingProperties = properties.filter((property) => {
    return property.name === name;
  });

  const propertyWithValue = matchingProperties.find((property) => {
    return property.value.value !== null;
  });

  return propertyWithValue?.value ?? matchingProperties[0]?.value;
}

function extract<T extends ComponentPropertyValue['type']>({
  value,
  type,
}: {
  value: ComponentPropertyValue;
  type: T;
}): SpecificComponentPropertyValue<T> | undefined {
  if (value.type !== type) {
    return undefined;
  }

  if (value.value === null) {
    return undefined;
  }

  // Compound types contain the `type` and `value` for each element
  // extract just the `value`.
  if (value.type === 'dictionary') {
    const record = value.value;
    let extractedRecord = {};
    for (const key in record) {
      const value = record[key];
      extractedRecord = {
        ...extractedRecord,
        [key]: value?.value ?? undefined,
      };
    }
    return extractedRecord as any;
  }

  return (value.value as any) ?? undefined;
}

export function read<T extends ComponentPropertyValue['type']>({
  name,
  component,
  parameters,
  type,
}: {
  name: string;
  component: Component;
  parameters?: FunctionParameter[];
  type: T;
}): SpecificComponentPropertyValue<T> | undefined {
  const property = readProperty({ name, component, parameters });
  if (property === undefined) {
    return undefined;
  }

  return extract({ value: property, type });
}

export function applyComponentStyle({
  component,
  parameters,
  componentStylesByName,
}: {
  component: Component;
  parameters?: FunctionParameter[];
  componentStylesByName: Record<string, ComponentStyleDefinition>;
}): Component {
  const styleName = read({
    name: 'style',
    component,
    parameters,
    type: 'string',
  });

  if (styleName === undefined || styleName === null) {
    return component;
  }

  const style = componentStylesByName[styleName];
  if (style === undefined) {
    return component;
  }

  return {
    ...component,
    properties: component.properties.concat(style.properties),
  };
}

/// Represents the ComponentPropertyValue.value when { type: T }, e.g.
/// SpecificComponentPropertyValue<'string'> === string | null
type SpecificComponentPropertyValue<T extends ComponentPropertyValue['type']> =
  Extract<ComponentPropertyValue, { type: T }>['value'];

type PropertyReader = <T extends ComponentPropertyValue['type']>({
  name,
  type,
}: {
  name: string;
  type: T;
}) => SpecificComponentPropertyValue<T> | undefined;

type PropertyAccessProps = {
  readProperty: PropertyReader;
  writeProperty: (_: ComponentProperty) => void;
  LogUnreadProperties: React.FC<{ children: React.ReactElement }>;
};

type LogUnreadPropertiesProps = {
  component: Component;
  readPropertyNames: Array<String>;
};
function LogUnreadPropertiesImpl({
  component,
  readPropertyNames,
  children,
}: React.PropsWithChildren<LogUnreadPropertiesProps>) {
  const { dispatch: componentDebugDispatch } = useComponentPreviewDebug();
  useEffect(() => {
    const unreadProperties = component.properties.filter(
      (property) => !readPropertyNames.includes(property.name),
    );

    if (unreadProperties.length === 0) {
      return;
    }

    componentDebugDispatch({
      type: 'mark-unread-properties',
      componentType: component.type,
      propertyNames: unreadProperties.map((p) => p.name),
    });
  }, [
    component.properties,
    component.type,
    componentDebugDispatch,
    readPropertyNames,
  ]);

  return <>{children}</>;
}

function useProperties({
  component,
  parameters,
}: {
  component: Component;
  parameters: FunctionParameter[];
}): PropertyAccessProps {
  const { dispatch: componentsPreviewDispatch, state: componentsPreviewState } =
    useComponentsPreview();
  const readPropertyNamesRef = useRef<string[]>([]);
  const readProperty = function <T extends ComponentPropertyValue['type']>({
    name,
    type,
  }: {
    name: string;
    type: T;
  }): SpecificComponentPropertyValue<T> | undefined {
    readPropertyNamesRef.current.push(name);

    const previewOverrideProperties =
      componentsPreviewState.componentPropertyOverridesByID[component.id] ?? [];
    const componentWithPreviewProperties: Component = {
      ...component,
      properties: previewOverrideProperties.concat(component.properties),
    };

    return read({
      name,
      type,
      component: componentWithPreviewProperties,
      parameters,
    });
  };

  const LogUnreadProperties = useCallback(function ({
    component,
    readPropertyNames,
  }: LogUnreadPropertiesProps): React.FC<{ children: React.ReactElement }> {
    return ({ children }) => (
      <LogUnreadPropertiesImpl
        component={component}
        readPropertyNames={readPropertyNames}
      >
        {children}
      </LogUnreadPropertiesImpl>
    );
  }, []);

  const writeProperty = useCallback(
    function (property: ComponentProperty) {
      componentsPreviewDispatch({
        type: 'update-preview-state',
        componentID: component.id,
        property,
      });
    },
    [component.id, componentsPreviewDispatch],
  );

  return {
    readProperty,
    writeProperty,
    LogUnreadProperties: LogUnreadProperties({
      component,
      readPropertyNames: readPropertyNamesRef.current,
    }),
  };
}

type ComponentPreviewProps = {
  component: Component;
  parameters: FunctionParameter[];
  insetForScreenEdges?: boolean;
};

export function ComponentPreview({
  component,
  parameters,
  insetForScreenEdges,
}: ComponentPreviewProps): React.ReactElement {
  const { state } = useComponentsPreview();

  const styledComponent = useMemo(() => {
    return applyComponentStyle({
      component,
      parameters,
      componentStylesByName: state.componentStylesByName,
    });
  }, [component, parameters, state.componentStylesByName]);

  switch (styledComponent.type) {
    case 'button': {
      return (
        <ButtonComponentPreview
          component={styledComponent}
          parameters={parameters}
        />
      );
    }
    case 'group':
      // TODO(LAB-512): This just renders as a vertical stack. Consider whether Group is necessary at all.
      return (
        <StackComponentPreview
          component={{
            ...styledComponent,
            properties: styledComponent.properties.concat({
              name: 'childLayoutAxis',
              value: { type: 'string', value: 'y' },
            }),
          }}
          parameters={parameters}
        />
      );
    case 'binaryToggle':
      return (
        <BinaryToggleComponentPreview
          component={styledComponent}
          parameters={parameters}
        />
      );
    case 'textInput':
      return (
        <TextInputComponentPreview
          component={styledComponent}
          parameters={parameters}
        />
      );
    case 'segmentedControl':
      return (
        <SegmentedControlComponentPreview
          component={styledComponent}
          parameters={parameters}
        />
      );
    case 'stack': {
      return (
        <StackComponentPreview
          component={styledComponent}
          parameters={parameters}
        />
      );
    }
    case 'image': {
      return (
        <ImageComponentPreview
          component={styledComponent}
          parameters={parameters}
        />
      );
    }
    case 'label': {
      return (
        <LabelComponentPreview
          component={styledComponent}
          parameters={parameters}
        />
      );
    }
    case 'list': {
      return (
        <ListComponentPreview
          component={styledComponent}
          parameters={parameters}
          insetForScreenEdges={insetForScreenEdges}
        />
      );
    }
    case 'function': {
      return (
        <FunctionComponentPreview
          component={styledComponent}
          parameters={parameters}
        />
      );
    }
    case 'tabBar': {
      return (
        <TabBarComponentPreview
          component={styledComponent}
          parameters={parameters}
        />
      );
    }
  }
}

function resolveDimension(
  propertyDictionary: Record<string, ComponentPropertyValue> | null | undefined,
): string | undefined {
  if (propertyDictionary === null || propertyDictionary === undefined) {
    return undefined;
  }

  if (propertyDictionary['type']?.type !== 'string') {
    return undefined;
  }

  if (propertyDictionary['type'].value === 'hug') {
    return '';
  }

  if (propertyDictionary['type'].value === 'value') {
    const valueObj = propertyDictionary['value'];
    if (valueObj?.type !== 'int' || valueObj?.value === null) {
      return undefined;
    }

    return `${valueObj.value}px`;
  }

  if (propertyDictionary['type'].value === 'fill_parent') {
    const paddingObj = propertyDictionary['padding'];
    if (paddingObj?.type !== 'int' || paddingObj?.value === null) {
      return undefined;
    }

    return `calc(100% - ${paddingObj.value}px)`;
  }

  return undefined;
}

function StyledText({
  text,
  textStyle,
  textColorOverride,
}: {
  text: string | null;
  textStyle: TextStyle | null;
  textColorOverride?: string;
}) {
  const textDetails = useTextStyle({ textStyle });
  const { resolveColor } = useColorPreview();
  const textColor =
    textColorOverride ??
    (textDetails?.textColor !== undefined
      ? resolveColor(textDetails.textColor)
      : undefined);
  return (
    <span
      style={{
        fontSize: textDetails?.fontSize,
        fontFamily: textDetails?.fontName,
        color: textColor,
      }}
    >
      {text}
    </span>
  );
}

// Individual Component Previews:

function SegmentedControlComponentPreview({
  component,
  parameters,
}: {
  component: Extract<Component, { type: LeafComponentType }>;
  parameters: FunctionParameter[];
}) {
  const { writeProperty, readProperty, LogUnreadProperties } = useProperties({
    component,
    parameters,
  });
  const segmentValues = readProperty({ name: 'segments', type: 'array' }) ?? [];
  const segments = segmentValues
    .map((value) => extract({ value, type: 'string' }))
    .filter((s) => s !== undefined && s !== null);

  // NOTE: Defaulting to `0` is a choice of SEAMantics - the segmented control
  // in iOS is generally expected to always have a selected segment, even if it
  // is POSSIBLE to not provide one.
  const selectedSegmentIndex =
    readProperty({ name: 'selectedSegmentIndex', type: 'int' }) ?? 0;

  const width = resolveDimension(
    readProperty({ name: 'width', type: 'dictionary' }),
  );

  // NOTE: Segmented controls in iOS always have the same height FWICT: 32px.
  // TODO: Either remove the height property, or log when it is set to a non-32 value.
  const height = 32;
  const segmentHeight = 28;
  const totalGapWidth =
    4 + // the width of the leading and trailing gap
    4 * (segments.length - 1); // the gap between each segment
  const segmentWidth = `calc((100% - (${totalGapWidth}px) / ${segments.length})`;
  // NOTE: This backgroundColor comes from iOS 26's figma library, it is
  // 767680 at 12% opacity
  const backgroundColor = `#7676801F`;
  return (
    <LogUnreadProperties>
      <Row
        style={{
          width,
          height,
          borderRadius: height / 2,
          backgroundColor,
          alignItems: 'center',
        }}
      >
        {segments.map((segment, i) => {
          const isSelected = i === selectedSegmentIndex;
          return (
            <Column
              key={segment}
              onClick={() => {
                writeProperty({
                  name: 'selectedSegmentIndex',
                  value: { type: 'int', value: i },
                });
              }}
              style={{
                width: segmentWidth,
                backgroundColor: isSelected ? 'white' : '',
                height: segmentHeight,
                borderRadius: segmentHeight / 2,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {segment}
            </Column>
          );
        })}
      </Row>
    </LogUnreadProperties>
  );
}

function TextInputComponentPreview({
  component,
  parameters,
}: {
  component: Extract<Component, { type: LeafComponentType }>;
  parameters: FunctionParameter[];
}) {
  const { writeProperty, readProperty, LogUnreadProperties } = useProperties({
    component,
    parameters,
  });
  const text =
    readProperty({
      name: 'text',
      type: 'string',
    }) ?? undefined;

  const placeholder =
    readProperty({
      name: 'placeholder',
      type: 'string',
    }) ?? undefined;

  const width = resolveDimension(
    readProperty({ name: 'width', type: 'dictionary' }),
  );

  const height = resolveDimension(
    readProperty({ name: 'height', type: 'dictionary' }),
  );

  const secure =
    readProperty({
      name: 'isSecureTextEntry',
      type: 'bool',
    }) ?? false;

  return (
    <LogUnreadProperties>
      <input
        style={{ width, height }}
        type={secure ? 'password' : 'text'}
        value={text}
        placeholder={placeholder}
        onChange={(e) => {
          writeProperty({
            name: 'text',
            value: { type: 'string', value: e.target.value },
          });
        }}
      />
    </LogUnreadProperties>
  );
}

function ButtonComponentPreview({
  component,
  parameters,
}: {
  component: Extract<Component, { type: LeafComponentType }>;
  parameters: FunctionParameter[];
}) {
  const { resolveColor } = useColorPreview();
  const { readProperty, LogUnreadProperties } = useProperties({
    component,
    parameters,
  });
  const bgColorValue = readProperty({
    name: 'backgroundColor',
    type: 'color',
  });

  const backgroundColor =
    bgColorValue === undefined ? undefined : resolveColor(bgColorValue);

  const textColorValue = readProperty({
    name: 'textColor',
    type: 'color',
  });
  const textColor =
    textColorValue === undefined ? undefined : resolveColor(textColorValue);

  const width = resolveDimension(
    readProperty({ name: 'width', type: 'dictionary' }),
  );
  const height = resolveDimension(
    readProperty({ name: 'height', type: 'dictionary' }),
  );

  const borderColorValue = readProperty({
    name: 'borderColor',
    type: 'color',
  });
  const borderColor =
    borderColorValue === undefined ? undefined : resolveColor(borderColorValue);
  const borderWidth = readProperty({ name: 'borderWidth', type: 'int' });
  const borderStyle = (() => {
    if (borderWidth === undefined || borderWidth === null) {
      return { border: 'none' };
    }

    if (borderColor === undefined) {
      return { border: 'none' };
    }

    return {
      borderWidth,
      borderColor,
      borderStyle: 'solid',
    };
  })();

  const buttonText = readProperty({
    name: 'text',
    type: 'string',
  });
  const symbolName = readProperty({
    name: 'symbolName',
    type: 'string',
  });
  const symbolIcon = resolveButtonSymbolIcon(symbolName ?? undefined);
  const hasText = Boolean(buttonText && buttonText.trim().length > 0);
  const content =
    buttonText && buttonText.trim().length > 0
      ? buttonText
      : symbolIcon
        ? (() => {
            const Icon = symbolIcon;
            return <Icon size={16} aria-label={symbolName ?? 'button icon'} />;
          })()
        : symbolName;

  return (
    <LogUnreadProperties>
      <button
        style={{
          backgroundColor,
          ...borderStyle,
          width: width ?? '120px',
          height: height ?? '44px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: hasText ? 6 : 0,
          borderRadius:
            readProperty({
              name: 'cornerRadius',
              type: 'int',
            }) ?? 0,
          color: textColor,
        }}
      >
        {content}
      </button>
    </LogUnreadProperties>
  );
}

function BinaryToggleComponentPreview({
  component,
  parameters,
}: {
  component: Extract<Component, { type: LeafComponentType }>;
  parameters: FunctionParameter[];
}) {
  const { writeProperty, readProperty, LogUnreadProperties } = useProperties({
    component,
    parameters,
  });

  const isOn = readProperty({ name: 'isOn', type: 'bool' }) ?? false;
  return (
    <LogUnreadProperties>
      <ToggleSwitch
        isOn={isOn}
        onChange={(isOn) => {
          writeProperty({ name: 'isOn', value: { type: 'bool', value: isOn } });
        }}
      />
    </LogUnreadProperties>
  );
}

function StackComponentPreview({
  component,
  parameters,
}: {
  component: Extract<Component, { type: ContainerComponentType }>;
  parameters: FunctionParameter[];
}) {
  const { resolveColor } = useColorPreview();
  const { state } = useComponentsPreview();
  const { readProperty, LogUnreadProperties } = useProperties({
    component,
    parameters,
  });

  const childLayoutAxis =
    readProperty({
      name: 'childLayoutAxis',
      type: 'string',
    }) ?? 'y';

  const spacing = readProperty({ name: 'spacing', type: 'int' }) ?? 0;
  const padding = readProperty({ name: 'padding', type: 'int' }) ?? undefined;
  const backgroundColorValue = readProperty({
    name: 'backgroundColor',
    type: 'color',
  });
  const backgroundColor =
    backgroundColorValue === undefined
      ? undefined
      : resolveColor(backgroundColorValue);
  const width = resolveDimension(
    readProperty({ name: 'width', type: 'dictionary' }),
  );
  const height = resolveDimension(
    readProperty({ name: 'height', type: 'dictionary' }),
  );

  return (
    <LogUnreadProperties>
      <div
        style={{
          display: 'flex',
          flexDirection: childLayoutAxis === 'y' ? 'column' : 'row',
          width: childLayoutAxis === 'y' ? '100%' : '',
          gap: spacing,
          padding,
          backgroundColor,
          height,
          ...(width === undefined ? {} : { width }),
        }}
      >
        {(component.childIDs ?? []).map((id) => {
          const childComponent = state.componentsByID[id];
          if (childComponent === undefined) {
            return <></>;
          }

          return (
            <ComponentPreview
              component={childComponent}
              parameters={parameters}
            />
          );
        })}
      </div>
    </LogUnreadProperties>
  );
}

function ImageComponentPreview({
  component,
  parameters,
}: {
  component: Extract<Component, { type: LeafComponentType }>;
  parameters: FunctionParameter[];
}) {
  const { readProperty, LogUnreadProperties } = useProperties({
    component,
    parameters,
  });
  const src = readProperty({ name: 'src', type: 'string' }) ?? undefined;
  const cornerRadius = readProperty({
    name: 'cornerRadius',
    type: 'int',
  });
  return (
    <LogUnreadProperties>
      <img
        src={src}
        width={'100%'}
        style={{ borderRadius: cornerRadius ?? 0 }}
      />
    </LogUnreadProperties>
  );
}

function LabelComponentPreview({
  component,
  parameters,
}: {
  component: Extract<Component, { type: LeafComponentType }>;
  parameters: FunctionParameter[];
}) {
  const { resolveColor } = useColorPreview();
  const { readProperty, LogUnreadProperties } = useProperties({
    component,
    parameters,
  });
  const text = readProperty({ name: 'text', type: 'string' }) ?? '';
  const textStyleName = readProperty({
    name: 'textStyle',
    type: 'string',
  });
  const textStyleValue = readProperty({
    name: 'textStyle',
    type: 'dictionary',
  });
  const textColorValue = readProperty({
    name: 'textColor',
    type: 'color',
  });
  const textColor =
    textColorValue === undefined ? undefined : resolveColor(textColorValue);
  const textAlignment =
    readProperty({
      name: 'textAlignment',
      type: 'string',
    }) ?? undefined;
  const textStyle: TextStyle | null =
    textStyleName !== undefined
      ? { type: 'style', style: textStyleName }
      : textStyleValue === undefined
        ? null
        : JSON.parse(JSON.stringify(textStyleValue));
  return (
    <LogUnreadProperties>
      <div
        style={{
          width: '100%',
          textAlign: textAlignment,
        }}
      >
        <StyledText
          text={text}
          textStyle={textStyle}
          textColorOverride={textColor}
        />
      </div>
    </LogUnreadProperties>
  );
}

function ListComponentPreview({
  component,
  parameters,
  insetForScreenEdges,
}: {
  component: Extract<Component, { type: LeafComponentType }>;
  parameters: FunctionParameter[];
  insetForScreenEdges?: boolean;
}) {
  const { resolveColor } = useColorPreview();
  const { state } = useComponentsPreview();
  const { readProperty, LogUnreadProperties } = useProperties({
    component,
    parameters,
  });
  const functionName = readProperty({
    name: 'functionName',
    type: 'string',
  });
  const items = readProperty({ name: 'items', type: 'array' }) ?? [];

  if (functionName === undefined) {
    return <></>;
  }
  const fnComponent = Object.values(state.functionComponentsByID).find(
    (fn) => fn.name === functionName,
  );
  if (fnComponent === undefined) {
    return <></>;
  }

  const templateComponent = state.componentsByID[fnComponent.templateID];
  if (templateComponent === undefined) {
    return <></>;
  }

  function TemplatedComponentFromItem({
    item,
    templateComponent,
    parameters,
  }: {
    item: Record<string, ComponentPropertyValue>;
    templateComponent: Component;
    parameters: FunctionParameter[];
  }) {
    const overridenParameters = useMemo(() => {
      return parameters.map((p) => {
        const override = item[p.name];
        if (override === undefined) {
          return p;
        }

        return {
          ...p,
          defaultValue: override,
        };
      });
    }, [parameters, item]);

    return (
      <LogUnreadProperties>
        <ComponentPreview
          component={templateComponent}
          parameters={overridenParameters}
        />
      </LogUnreadProperties>
    );
  }

  const backgroundColorValue = readProperty({
    name: 'backgroundColor',
    type: 'color',
  }) ?? { type: 'reference', reference: 'background.primary' };
  const backgroundColor = resolveColor(backgroundColorValue);

  const spacingValue =
    readProperty({
      name: 'spacing',
      type: 'int',
    }) ?? 0;
  const padding = insetForScreenEdges === true ? 16 : 0;
  return (
    <Column
      className="list-scroll"
      style={{
        height: '100%',
        backgroundColor,
        padding,
        alignItems: 'center',
        gap: spacingValue,
      }}
    >
      {items.map((item, i) => {
        if (item.type !== 'dictionary' || item.value === null) {
          return <></>;
        }
        return (
          <TemplatedComponentFromItem
            key={i}
            item={item.value}
            parameters={fnComponent.parameters}
            templateComponent={templateComponent}
          />
        );
      })}
    </Column>
  );
}

function FunctionComponentPreview({
  component,
  parameters,
}: {
  component: Extract<Component, { type: LeafComponentType }>;
  parameters: FunctionParameter[];
}) {
  const { state } = useComponentsPreview();
  const { readProperty, LogUnreadProperties } = useProperties({
    component,
    parameters,
  });
  const functionName = readProperty({
    name: 'name',
    type: 'string',
  });

  const fnArgs: ComponentPropertyValue[] =
    readProperty({
      name: 'arguments',
      type: 'array',
    }) ?? [];

  if (functionName === undefined) {
    return <></>;
  }

  const fnComponent = Object.values(state.functionComponentsByID).find(
    (fn) => fn.name === functionName,
  );
  if (fnComponent === undefined) {
    return <></>;
  }

  const templateComponent = state.componentsByID[fnComponent.templateID];
  if (templateComponent === undefined) {
    return <></>;
  }

  const argumentOverrides: Record<string, ComponentPropertyValue | undefined> =
    fnArgs.reduce((acc, e) => {
      if (e.type !== 'dictionary' || e.value === null) {
        return acc;
      }

      const nameValue = e.value['name'];
      if (nameValue?.type !== 'string' || nameValue?.value === null) {
        return acc;
      }

      const argValue = e.value['value'];
      if (argValue === null) {
        return acc;
      }
      return {
        ...acc,
        [nameValue.value]: argValue,
      };
    }, {});

  const overridenParameters = fnComponent.parameters.map((p) => {
    const override = argumentOverrides[p.name];
    if (override === undefined) {
      return p;
    }

    return {
      ...p,
      defaultValue: override,
    };
  });

  return (
    <LogUnreadProperties>
      <ComponentPreview
        component={templateComponent}
        parameters={overridenParameters}
      />
    </LogUnreadProperties>
  );
}

function TabBarComponentPreview({
  component,
  parameters,
}: {
  component: Extract<Component, { type: LeafComponentType }>;
  parameters: FunctionParameter[];
}) {
  const { readProperty, LogUnreadProperties } = useProperties({
    component,
    parameters,
  });
  const [selectedTabID, setSelectedTabID] = useState<ScreenID | null>(null);

  const tabs = readProperty({ name: 'tabs', type: 'array' }) ?? [];

  function fallbackTabScreenID(
    tabs: ComponentPropertyValue[],
  ): ScreenID | null {
    const firstTab = tabs[0];
    if (
      firstTab === undefined ||
      firstTab.type !== 'dictionary' ||
      firstTab.value === null
    ) {
      return null;
    }
    const screenIDValue = firstTab.value['screenID'];
    if (screenIDValue === undefined || screenIDValue.type !== 'string') {
      return null;
    }
    return screenIDValue.value;
  }

  const visibleScreenID = selectedTabID ?? fallbackTabScreenID(tabs);

  function VisibleTab({ screenID }: { screenID: ScreenID | null }) {
    const { state } = useComponentsPreview();
    const { state: screenState } = useScreenPreview();
    const rootComponent = useMemo(() => {
      if (screenID === null) {
        return undefined;
      }

      const screen = screenState.screensByID[screenID];
      if (screen === undefined) {
        return undefined;
      }

      const rootComponentID = screen.rootComponentID;
      if (rootComponentID === undefined) {
        return undefined;
      }
      const rootComponent = state.componentsByID[rootComponentID];
      return rootComponent;
    }, [screenID, screenState.screensByID, state.componentsByID]);

    if (rootComponent === undefined) {
      return <></>;
    }

    return (
      <div style={{ height: `calc(100% - ${tabHeight}px)` }}>
        <ComponentPreview
          component={rootComponent}
          parameters={[]}
          insetForScreenEdges={true}
        />
      </div>
    );
  }
  const tabHeight = 64;

  const tabColor = '#F0F0F0';
  return (
    <LogUnreadProperties>
      <Column
        style={{ alignItems: 'center', height: '100%', overflowY: 'clip' }}
      >
        <VisibleTab screenID={visibleScreenID} />
        <Row
          style={{
            height: tabHeight,
            backgroundColor: tabColor,
            width: '100%',
            alignItems: 'center',
          }}
        >
          {tabs.map((tab) => {
            if (tab.type !== 'dictionary' || tab.value === null) {
              return <></>;
            }

            const imgValue = tab.value['src'];
            const img = imgValue?.type === 'string' ? imgValue.value : null;

            const nameValue = tab.value['name'];
            const name = nameValue?.type === 'string' ? nameValue.value : null;

            const screenIDValue = tab.value['screenID'];
            const screenID =
              screenIDValue?.type === 'string' ? screenIDValue.value : null;

            const textStyleValue = tab.value['textStyle'];
            const textStyle: TextStyle | null =
              textStyleValue?.type === 'dictionary'
                ? JSON.parse(JSON.stringify(textStyleValue?.value))
                : null;

            return (
              <Column
                key={screenID}
                style={{
                  alignItems: 'center',
                  flexGrow: '1',
                  flexBasis: `calc(100% / ${tabs.length})`,
                }}
                onClick={() => {
                  setSelectedTabID(screenID);
                }}
              >
                {img && <SystemImage src={img} />}
                {name && <StyledText text={name} textStyle={textStyle} />}
              </Column>
            );
          })}
          <Spacer />
        </Row>
      </Column>
    </LogUnreadProperties>
  );
}

function FunctionParameterValue({
  functionID,
  parameterName,
  value,
}: {
  functionID: FunctionComponentID;
  parameterName: string;
  value?: ComponentPropertyValue;
}) {
  const { state, dispatch } = useComponentsPreview();
  const { resolveColor, state: colorState } = useColorPreview();

  const previewValue =
    state.functionParameterPreviewValuesByID[functionID]?.[parameterName] ??
    value;

  const colorNames = useMemo(() => {
    return colorState.colors.map((c) => c.name);
  }, [colorState.colors]);

  if (previewValue === undefined) {
    return <Row style={{ backgroundColor: 'white' }}>{'<null>'}</Row>;
  }

  // TODO(LAB-462): Add support for mutating all primitive types
  switch (previewValue.type) {
    case 'color':
      const resolvedColor = resolveColor(previewValue.value);
      return (
        <Row>
          {(previewValue.value === null ||
            previewValue.value.type === 'reference') && (
            <TypeaheadInput
              style={{
                padding: 4,
                width: '100%',
              }}
              value={previewValue?.value?.reference ?? ''}
              onChange={(colorName) => {
                dispatch({
                  type: 'update-parameter-preview-value',
                  functionID,
                  parameterName,
                  value: {
                    type: 'color',
                    value: { type: 'reference', reference: colorName },
                  },
                });
              }}
              placeholder={parameterName}
              options={colorNames}
              renderOption={TextColorOption}
              renderOptionIndicator={ColorIndicator}
            />
          )}
          {previewValue.value?.type === 'value' && <div>{resolvedColor}</div>}
        </Row>
      );
    case 'string':
      const displayValue =
        previewValue.value === null ? '' : previewValue.value;
      return (
        <Row>
          <PaletteInput
            fillWidth={true}
            type="text"
            value={displayValue}
            placeholder={parameterName}
            onChange={(value) => {
              dispatch({
                type: 'update-parameter-preview-value',
                functionID,
                parameterName,
                value: {
                  type: 'string',
                  value,
                },
              });
            }}
          />
        </Row>
      );
    case 'bool':
      return (
        <Row>
          <>{previewValue.value ? 'true' : 'false'}</>
        </Row>
      );
    case 'int':
      return (
        <Row>
          <PaletteInput
            fillWidth={true}
            type="number"
            value={`${previewValue?.value ?? 0}`}
            placeholder={parameterName}
            onChange={(value) => {
              const intValue = parseInt(value);
              dispatch({
                type: 'update-parameter-preview-value',
                functionID,
                parameterName,
                value: {
                  type: 'int',
                  value: intValue,
                },
              });
            }}
          />
        </Row>
      );
    case 'dictionary':
      const dictionary = previewValue.value;
      const fields = dictionary ? Object.keys(dictionary) : [];
      return (
        <Column>
          {fields.map((key) => (
            <Row key={key}>
              <>{key}</>
              {/* TODO(LAB-463): Handle container Values. Can't naively pass `propertyName`, closer to a keypath. Maybe pass a `writer` prop? */}
              <FunctionParameterValue value={dictionary[key]} />
            </Row>
          ))}
        </Column>
      );
    case 'array':
      const elements = previewValue.value ?? [];
      return (
        <Column style={{ backgroundColor: 'white' }}>
          {/* TODO(LAB-463): Handle container Values. Can't naively pass `propertyName`, closer to a keypath. Maybe pass a `writer` prop? */}
          {elements.map((value, i) => (
            <FunctionParameterValue key={i} value={value} />
          ))}
        </Column>
      );
  }
}

function FunctionParameter({
  functionID,
  parameter,
  templateID,
}: {
  functionID: FunctionComponentID;
  parameter: FunctionParameter;
  templateID: ComponentID;
}) {
  const { state } = useComponentsPreview();

  const boundComponentID = parameter.binding.componentID ?? templateID;
  const boundComponent = state.componentsByID[boundComponentID];
  const boundProperty = boundComponent?.properties.filter(
    (p) => p.name === parameter.binding.propertyName,
  )[0];

  /*
  `boundValue` represents the value of the property bound to the FunctionParameter.

  That property may have an associated value, for example if:
  - The property was explicitly written to with writeProperties
  - The property has a default value from SEAM (e.g. childLayoutAxis defaults to 'y').

  If the property has an associated value, we use THAT value.
  The `defaultValue` is the value associated with the `FunctionParameter`,
  we use that if the property does not have a value associated with it.
  
  */
  // TODO(LAB-503): I am unclear if this is good SEAMantics: we may want the `defaultValue` to override the proeprty's value.
  function resolveValue({
    boundValue,
    defaultValue,
  }: {
    boundValue: ComponentPropertyValue | undefined;
    defaultValue: ComponentPropertyValue | undefined;
  }): ComponentPropertyValue | undefined {
    if (boundValue === undefined) {
      // TODO(LAB-464): Handle FunctionParameter binding resolution failures, for now no-op
      return;
    }

    if (boundValue.value === null && boundValue.type === defaultValue?.type) {
      return {
        ...boundValue,
        value: defaultValue?.value as any,
      };
    }

    return boundValue;
  }

  const resolvedValue = resolveValue({
    boundValue: boundProperty?.value,
    defaultValue: parameter.defaultValue,
  });

  return (
    <Column
      style={{
        ...ThinBorderStyle,
        marginInline: 4,
        padding: 4,
        marginBlockEnd: 8,
      }}
    >
      <div style={{ paddingInline: 4 }}>
        <Font kind="font-caption">{parameter.name}</Font>
      </div>
      <FunctionParameterValue
        functionID={functionID}
        parameterName={parameter.name}
        value={resolvedValue}
      />
    </Column>
  );
}

export function ComponentsPreview({
  component,
}: {
  component: FunctionComponent;
}) {
  const { state } = useComponentsPreview();
  const [showProperties, setShowProperties] = useState(false);

  const templateComponent = state.componentsByID[component.templateID];
  const parameterValues =
    state.functionParameterPreviewValuesByID[component.id];

  const parameters = useMemo(() => {
    if (parameterValues === undefined) {
      return component.parameters;
    }

    return component.parameters.map((p) => {
      const overridenValue = parameterValues[p.name];
      if (overridenValue === undefined) {
        return p;
      }
      return {
        ...p,
        defaultValue: overridenValue,
      };
    });
  }, [component.parameters, parameterValues]);

  return (
    <Column style={{ ...ThinBorderStyle, padding: 4 }}>
      <Row style={{ justifyContent: 'center' }}>
        <Font kind="font-body">{component.name}</Font>
      </Row>
      <Row style={{ justifyContent: 'center' }}>
        {templateComponent && (
          <ComponentPreview
            component={templateComponent}
            parameters={parameters}
          />
        )}
      </Row>

      <Column
        style={{
          borderTop: ThinBorderStyle.border,
          paddingInline: -4,
          marginBlockStart: 8,
          backgroundColor: '#ffffff',
        }}
      >
        <div
          onClick={() => {
            setShowProperties(!showProperties);
          }}
        >
          <Row style={{ alignItems: 'center' }}>
            {showProperties && <FaChevronUp />}
            {showProperties || <FaChevronDown />}
            <Font kind="font-caption">Parameters</Font>
          </Row>
        </div>
        {showProperties &&
          component.parameters.map((parameter) => (
            <FunctionParameter
              key={parameter.name}
              functionID={component.id}
              parameter={parameter}
              templateID={component.templateID}
            />
          ))}
      </Column>
    </Column>
  );
}
